
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Users, Calendar, MapPin, Award, Plus, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Fellowship {
  id: string;
  title: string;
  description: string;
  duration: string;
  location: string;
  deadline: string;
  spots: number;
  requirements: string[];
  category: string;
  status: 'open' | 'closed' | 'applied';
}

const TeenFellowshipPrograms: React.FC = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [fellowships, setFellowships] = useState<Fellowship[]>([]);

  const categories = ['all', 'Technology', 'Social Impact', 'Business', 'Healthcare', 'Environment'];

  const filteredFellowships = fellowships.filter(fellowship => {
    const matchesSearch = fellowship.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fellowship.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || fellowship.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const applyToFellowship = (fellowshipId: string) => {
    setFellowships(fellowships.map(f => 
      f.id === fellowshipId ? { ...f, status: 'applied' as const } : f
    ));
    toast({
      title: "Application Submitted!",
      description: "Your fellowship application has been submitted successfully."
    });
  };

  const withdrawApplication = (fellowshipId: string) => {
    setFellowships(fellowships.map(f => 
      f.id === fellowshipId ? { ...f, status: 'open' as const } : f
    ));
    toast({
      title: "Application Withdrawn",
      description: "Your application has been withdrawn."
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-green-500" />
            Teen-Only Fellowship Programs
          </CardTitle>
          <CardDescription>
            Exclusive fellowship opportunities designed specifically for teenage innovators and leaders.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search and Filter */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Search fellowships..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="p-2 border rounded-md"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>

          {/* Fellowship Cards */}
          <div className="space-y-4">
            {filteredFellowships.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 mx-auto mb-4 text-green-500" />
                <h3 className="text-xl font-semibold mb-2">No Fellowship Programs Available</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Fellowship opportunities will appear here when they become available. Check back regularly for new exclusive programs.
                </p>
                <Button className="bg-green-500 hover:bg-green-600">
                  <Plus className="h-4 w-4 mr-2" />
                  Get Notified of New Programs
                </Button>
              </div>
            ) : (
              filteredFellowships.map((fellowship) => (
                <Card key={fellowship.id} className="border-l-4 border-l-green-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold">{fellowship.title}</h3>
                        <Badge className="mt-1 bg-green-100 text-green-800">
                          {fellowship.category}
                        </Badge>
                      </div>
                      <Badge variant={fellowship.status === 'applied' ? 'default' : 'outline'}>
                        {fellowship.status === 'applied' ? 'Applied' : `${fellowship.spots} spots`}
                      </Badge>
                    </div>

                    <p className="text-muted-foreground mb-4">{fellowship.description}</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{fellowship.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{fellowship.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Deadline: {fellowship.deadline}</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Requirements:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {fellowship.requirements.map((req, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-green-500 rounded-full"></span>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex gap-2">
                      {fellowship.status === 'applied' ? (
                        <Button 
                          variant="outline"
                          onClick={() => withdrawApplication(fellowship.id)}
                          className="border-red-500 text-red-500 hover:bg-red-50"
                        >
                          Withdraw Application
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => applyToFellowship(fellowship.id)}
                          className="bg-green-500 hover:bg-green-600"
                          disabled={fellowship.status === 'closed'}
                        >
                          Apply Now
                        </Button>
                      )}
                      <Button variant="outline">
                        Learn More
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeenFellowshipPrograms;
